import React, { useMemo, memo } from 'react';
import { Table as AntTable } from 'antd';
import { RightOutlined, DownOutlined } from '@ant-design/icons';
import { genNumberTable } from '../../../../function/genNumberTable';
import dayjs from 'dayjs';

const headerStyle = {
  color: 'white',
  fontWeight: 'bold',
  backgroundColor: '#4a4a4a',
  textAlign: 'center',
};

const expandHeaderStyle = {
  color: 'white',
  fontWeight: 'bold',
  backgroundColor: '#686868ff',
  textAlign: 'center',
};

const CASE_KEYS = ['deposit', 'withdraw', 'ETC', 'Panel', 'Register'];

const CASE_LABELS = {
  deposit: 'Deposit',
  withdraw: 'Withdraw',
  ETC: 'ETC.',
  Panel: 'Panel',
  Register: 'Register',
};

const toByCaseMap = (byCase = []) =>
  byCase.reduce((acc, item) => {
    acc[item.caseName] = item;
    return acc;
  }, {});

const formatWorkloadPercent = (workload, percent) => {
  const value = Number(workload) || 0;
  const pct = Number(percent) || 0;
  return (
    <span style={{ color: '#333', fontWeight: 500 }}>
      {value.toLocaleString()} ({pct.toFixed(2)}%)
    </span>
  );
};

const renderCaseCell = (byCaseMap, caseKey) =>
  formatWorkloadPercent(
    byCaseMap[caseKey]?.workload,
    byCaseMap[caseKey]?.percent
  );

const buildCaseColumns = (caseKey, title, getByCaseMap) => ({
  title,
  align: 'center',
  onHeaderCell: () => ({ style: headerStyle }),
  children: [
    {
      title: 'Workload',
      key: `${caseKey}_workload`,
      align: 'center',
      width: 110,
      onHeaderCell: () => ({ style: headerStyle }),
      render: (_, record) => renderCaseCell(getByCaseMap(record), caseKey),
    },
  ],
});

const MAIN_COLUMNS = [
  {
    title: 'ลำดับ',
    dataIndex: 'number',
    key: 'number',
    align: 'center',
    width: 80,
    onHeaderCell: () => ({ style: headerStyle }),
  },
  {
    title: 'พนักงาน',
    dataIndex: 'finish_by_name',
    key: 'finish_by_name',
    align: 'center',
    width: 160,
    onHeaderCell: () => ({ style: headerStyle }),
  },
  {
    title: 'ทั้งหมด',
    align: 'center',
    onHeaderCell: () => ({ style: headerStyle }),
    children: [
      {
        title: 'Workload',
        dataIndex: 'totalWorkload',
        key: 'totalWorkload',
        align: 'center',
        width: 130,
        onHeaderCell: () => ({ style: headerStyle }),
        render: (_, record) =>
          formatWorkloadPercent(record.totalWorkload, record.totalPercent),
      },
    ],
  },
  ...CASE_KEYS.map((caseKey) =>
    buildCaseColumns(caseKey, CASE_LABELS[caseKey], (record) => record.byCaseMap)
  ),
];

const EXPAND_COLUMNS = [
  {
    title: 'วันที่',
    dataIndex: 'date',
    key: 'date',
    align: 'center',
    width: 120,
    onHeaderCell: () => ({ style: expandHeaderStyle }),
    render: (text) => dayjs(text).format('DD/MM/YYYY'),
  },
  {
    title: 'ทั้งหมด',
    key: 'dailyTotal',
    align: 'center',
    width: 130,
    onHeaderCell: () => ({ style: expandHeaderStyle }),
    render: (_, record) => formatWorkloadPercent(record.workload, record.percent),
  },
  ...CASE_KEYS.map((caseKey) => ({
    title: CASE_LABELS[caseKey],
    key: `daily_${caseKey}`,
    align: 'center',
    width: 110,
    onHeaderCell: () => ({ style: expandHeaderStyle }),
    render: (_, record) => renderCaseCell(record.byCaseMap, caseKey),
  })),
];

const SystemSummary = memo(({ grandTotal = 0, systemByCase = [] }) => (
  <div
    style={{
      marginBottom: 16,
      padding: 16,
      background: '#f0f7ff',
      borderRadius: 8,
      border: '1px solid #d6e8ff',
    }}
  >
    <div style={{ fontSize: 16, fontWeight: 700, color: '#1a3a5c', marginBottom: 12 }}>
      Workload ทั้งระบบ: {Number(grandTotal || 0).toLocaleString()}
    </div>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: 12,
      }}
    >
      {systemByCase.map((item) => (
        <div
          key={item.caseName}
          style={{
            background: '#fff',
            borderRadius: 6,
            padding: '10px 12px',
            border: '1px solid #e8e8e8',
          }}
        >
          <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>
            {CASE_LABELS[item.caseName] || item.caseLabel}
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#333' }}>
            {Number(item.workload || 0).toLocaleString()}{' '}
            <span style={{ fontSize: 13, fontWeight: 500, color: '#1890ff' }}>
              ({Number(item.percent || 0).toFixed(2)}%)
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
));

const prepareTableData = (data) =>
  genNumberTable(data).map((row) => ({
    ...row,
    byCaseMap: toByCaseMap(row.byCase),
    daily: (row.daily || []).map((dailyRow) => ({
      ...dailyRow,
      byCaseMap: toByCaseMap(dailyRow.byCase),
    })),
  }));

const Table = ({ data = [], grandTotal = 0, systemByCase = [], loading = false }) => {
  const tableData = useMemo(() => prepareTableData(data), [data]);

  const expandedRowRender = useMemo(
    () => (record) => (
      <AntTable
        columns={EXPAND_COLUMNS}
        dataSource={record.daily}
        pagination={false}
        rowKey="date"
        size="small"
        bordered
      />
    ),
    []
  );

  return (
    <div className="overflow-x-auto">
      <SystemSummary grandTotal={grandTotal} systemByCase={systemByCase} />
      <AntTable
        loading={loading}
        dataSource={tableData}
        columns={MAIN_COLUMNS}
        expandable={{
          expandedRowRender,
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <DownOutlined onClick={(e) => onExpand(record, e)} style={{ fontSize: 20 }} />
            ) : (
              <RightOutlined onClick={(e) => onExpand(record, e)} style={{ fontSize: 20 }} />
            ),
        }}
        rowKey="finish_by"
        scroll={{ x: 'max-content' }}
        bordered
        pagination={{
          position: ['topRight'],
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          showSizeChanger: true,
          showQuickJumper: true,
        }}
      />
      <style>{`
        .ant-table-tbody > tr:hover > td {
          background-color: #e8f4f8 !important;
        }
        .ant-table-thead > tr > th.ant-table-row-expand-icon-cell {
          background-color: #4a4a4a !important;
          color: white !important;
        }
      `}</style>
    </div>
  );
};

export default memo(Table);
